import React, { useState, useEffect } from 'react';
import {
  Menu, X, ChevronDown, ChevronRight,
  MessageSquare, Bot, Zap, Shield,
  Users, TrendingUp, Clock, Globe,
  Check, ArrowRight, Play, Star,
  MessageCircle, ShoppingCart, Headphones
} from 'lucide-react';
import Logo from './Logo';

// Translations
const translations = {
  vi: {
    nav: {
      features: 'Tính Năng',
      howItWorks: 'Cách Hoạt Động',
      pricing: 'Bảng Giá',
      contact: 'Liên Hệ',
      getStarted: 'Bắt Đầu Ngay'
    },
    hero: {
      badge: 'Cloud9 + OpenClaw',
      title1: 'AI Agent Cho',
      title2: 'Zalo',
      subtitle: 'Tự động hóa chăm sóc khách hàng và bán hàng 24/7 với trợ lý AI thông minh. Tích hợp trực tiếp với Zalo để mang lại trải nghiệm tốt nhất cho khách hàng của bạn.',
      cta1: 'Dùng Thử Miễn Phí',
      cta2: 'Xem Demo',
      stat1: '10,000+',
      stat1Label: 'Tin Nhắn/Ngày',
      stat2: '99.9%',
      stat2Label: 'Độ Chính Xác',
      stat3: '24/7',
      stat3Label: 'Hỗ Trợ Liên Tục',
      stat4: '5 Phút',
      stat4Label: 'Cài Đặt'
    },
    features: {
      title: 'Tính Năng Mạnh Mẽ',
      subtitle: 'Mọi thứ bạn cần để tự động hóa chăm sóc khách hàng',
      feature1: {
        title: 'Trả Lời Tự Động',
        desc: 'AI hiểu ngữ cảnh và trả lời chính xác các câu hỏi của khách hàng 24/7'
      },
      feature2: {
        title: 'Bán Hàng Thông Minh',
        desc: 'Tự động tư vấn sản phẩm, xử lý đơn hàng và chốt sale'
      },
      feature3: {
        title: 'Tích Hợp Zalo',
        desc: 'Kết nối trực tiếp với Zalo OA, hỗ trợ hình ảnh, file và location'
      },
      feature4: {
        title: 'Phân Tích & Báo Cáo',
        desc: 'Dashboard chi tiết theo dõi hiệu suất và hành vi khách hàng'
      },
      feature5: {
        title: 'Multi-language',
        desc: 'Hỗ trợ tiếng Việt, tiếng Anh và nhiều ngôn ngữ khác'
      },
      feature6: {
        title: 'Bảo Mật Cao',
        desc: 'Dữ liệu được mã hóa, tuân thủ các tiêu chuẩn bảo mật'
      }
    },
    howItWorks: {
      title: 'Cách Hoạt Động',
      subtitle: 'Chỉ 3 bước đơn giản để bắt đầu',
      step1: {
        title: 'Kết Nối Zalo OA',
        desc: 'Liên kết tài khoản Zalo Official Account của bạn với Claw9'
      },
      step2: {
        title: 'Huấn Luyện AI',
        desc: 'Upload dữ liệu sản phẩm, FAQ và kịch bản bán hàng'
      },
      step3: {
        title: 'Khởi Chạy',
        desc: 'AI bắt đầu tự động trả lời và bán hàng trên Zalo'
      }
    },
    useCases: {
      title: 'Phù Hợp Cho Mọi Ngành Nghề',
      case1: {
        title: 'E-commerce',
        desc: 'Tự động tư vấn sản phẩm, xử lý đơn hàng, tracking'
      },
      case2: {
        title: 'Dịch Vụ',
        desc: 'Đặt lịch hẹn, tư vấn dịch vụ, chăm sóc khách hàng'
      },
      case3: {
        title: 'Bất Động Sản',
        desc: 'Tư vấn dự án, đặt lịch xem nhà, báo giá tự động'
      },
      case4: {
        title: 'Giáo Dục',
        desc: 'Tư vấn khóa học, hỗ trợ học viên, thu học phí'
      }
    },
    testimonials: {
      title: 'Khách Hàng Nói Gì',
      testimonial1: {
        quote: 'Claw9 đã giúp chúng tôi tăng 300% tỷ lệ chuyển đổi trên Zalo. AI trả lời nhanh và chính xác hơn cả nhân viên.',
        author: 'Nguyễn Văn A',
        role: 'CEO, Shop Online'
      },
      testimonial2: {
        quote: 'Tiết kiệm 80% thời gian chăm sóc khách hàng. Giờ team có thể tập trung vào việc phát triển sản phẩm.',
        author: 'Trần Thị B',
        role: 'Founder, Startup Tech'
      },
      testimonial3: {
        quote: 'Tích hợp đơn giản, AI học rất nhanh. Chỉ sau 1 tuần đã có thể xử lý 90% câu hỏi của khách.',
        author: 'Lê Văn C',
        role: 'Manager, Real Estate'
      }
    },
    cta: {
      title: 'Sẵn Sàng Tự Động Hóa?',
      subtitle: 'Bắt đầu miễn phí ngay hôm nay. Không cần thẻ tín dụng.',
      button: 'Dùng Thử Miễn Phí',
      feature1: 'Miễn phí 14 ngày',
      feature2: 'Không cần thẻ tín dụng',
      feature3: 'Hỗ trợ 24/7'
    },
    pricing: {
      eyebrow: 'Gói triển khai',
      title: 'Claw9 cho Zalo',
      subtitle: 'Thiết lập nhanh, vận hành gọn, trải nghiệm cao cấp.',
      setupLabel: 'Phí thiết lập',
      setupPrice: '2 triệu VNĐ',
      monthlyLabel: 'Phí sử dụng',
      monthlyPrice: '750k / tháng',
      button: 'Đăng ký tư vấn',
      features: [
        '1 AI Agent CSKH 24/7 + sync CRM',
        '1 AI Agent báo cáo phân tích mỗi ngày',
        'Huấn luyện dễ dàng qua UI',
        'Tự cải thiện không ngừng',
        'Người thật tiếp quản mọi lúc',
      ],
      calculatorTitle: 'So sánh chi phí',
      calculatorSubtitle: 'Nhìn ngay chi phí hiện tại so với Claw9.',
      repCount: 'Số nhân sự CSKH',
      crmToggle: 'Đang trả CRM riêng',
      currentStack: 'Cách làm hiện tại',
      claw9Stack: 'Với Claw9',
      repCost: 'Nhân sự CSKH',
      crmCost: 'CRM',
      adminCost: 'Vận hành thủ công',
      monthlyTotal: 'Tổng / tháng',
      firstMonth: 'Tháng đầu',
      savings: 'Tiết kiệm mỗi tháng',
      comparisonTitle: 'Vì sao Claw9 tốt hơn',
      comparisonRows: [
        ['Trả lời Zalo', 'Nhân sự online theo ca', 'AI trả lời 24/7'],
        ['CRM', 'Mua riêng', 'Đã có sẵn'],
        ['Huấn luyện bot', 'Khó chỉnh sửa', 'UI đơn giản'],
        ['Cải thiện theo thời gian', 'Phụ thuộc người vận hành', 'Tự cải thiện'],
        ['Can thiệp người thật', 'Thủ công', 'Vào bất kỳ lúc nào'],
      ],
    },
    footer: {
      companyName: 'CÔNG TY TNHH CLAW9 VIỆT NAM',
      address: '104 Mai Thị Lựu, Phường Tân Định, TP HCM',
      product: 'Sản Phẩm',
      features: 'Tính Năng',
      pricing: 'Bảng Giá',
      integrations: 'Tích Hợp',
      company: 'Công Ty',
      about: 'Về Chúng Tôi',
      careers: 'Tuyển Dụng',
      contact: 'Liên Hệ',
      resources: 'Tài Nguyên',
      docs: 'Tài Liệu',
      blog: 'Blog',
      support: 'Hỗ Trợ',
      legal: 'Pháp Lý',
      terms: 'Điều Khoản Sử Dụng',
      privacy: 'Chính Sách Bảo Mật',
      copyright: '© 2026 Claw9.ai. Bảo lưu mọi quyền.'
    }
  },
  en: {
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
      contact: 'Contact',
      getStarted: 'Get Started'
    },
    hero: {
      badge: 'Cloud9 + OpenClaw',
      title1: 'AI Agent For',
      title2: 'Zalo',
      subtitle: 'Automate customer support and sales 24/7 with intelligent AI assistants. Directly integrated with Zalo to deliver the best experience for your customers.',
      cta1: 'Try For Free',
      cta2: 'Watch Demo',
      stat1: '10,000+',
      stat1Label: 'Messages/Day',
      stat2: '99.9%',
      stat2Label: 'Accuracy',
      stat3: '24/7',
      stat3Label: 'Always On',
      stat4: '5 Min',
      stat4Label: 'Setup'
    },
    features: {
      title: 'Powerful Features',
      subtitle: 'Everything you need to automate customer care',
      feature1: {
        title: 'Auto Response',
        desc: 'AI understands context and accurately answers customer questions 24/7'
      },
      feature2: {
        title: 'Smart Selling',
        desc: 'Automatically consult products, process orders and close sales'
      },
      feature3: {
        title: 'Zalo Integration',
        desc: 'Direct connection with Zalo OA, supporting images, files and location'
      },
      feature4: {
        title: 'Analytics & Reports',
        desc: 'Detailed dashboard tracking performance and customer behavior'
      },
      feature5: {
        title: 'Multi-language',
        desc: 'Support Vietnamese, English and many other languages'
      },
      feature6: {
        title: 'High Security',
        desc: 'Data encrypted, compliant with security standards'
      }
    },
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Just 3 simple steps to get started',
      step1: {
        title: 'Connect Zalo OA',
        desc: 'Link your Zalo Official Account with Claw9'
      },
      step2: {
        title: 'Train AI',
        desc: 'Upload product data, FAQ and sales scripts'
      },
      step3: {
        title: 'Launch',
        desc: 'AI starts auto-responding and selling on Zalo'
      }
    },
    useCases: {
      title: 'Perfect For Every Industry',
      case1: {
        title: 'E-commerce',
        desc: 'Auto product consulting, order processing, tracking'
      },
      case2: {
        title: 'Services',
        desc: 'Appointment booking, service consulting, customer care'
      },
      case3: {
        title: 'Real Estate',
        desc: 'Project consulting, house viewing booking, auto pricing'
      },
      case4: {
        title: 'Education',
        desc: 'Course consulting, student support, fee collection'
      }
    },
    testimonials: {
      title: 'What Customers Say',
      testimonial1: {
        quote: 'Claw9 helped us increase conversion rate by 300% on Zalo. AI responds faster and more accurately than staff.',
        author: 'Nguyen Van A',
        role: 'CEO, Online Shop'
      },
      testimonial2: {
        quote: 'Saved 80% customer care time. Now the team can focus on product development.',
        author: 'Tran Thi B',
        role: 'Founder, Tech Startup'
      },
      testimonial3: {
        quote: 'Simple integration, AI learns very fast. After just 1 week it can handle 90% of customer questions.',
        author: 'Le Van C',
        role: 'Manager, Real Estate'
      }
    },
    cta: {
      title: 'Ready To Automate?',
      subtitle: 'Start free today. No credit card required.',
      button: 'Try For Free',
      feature1: '14 days free',
      feature2: 'No credit card',
      feature3: '24/7 support'
    },
    pricing: {
      eyebrow: 'Deployment Plan',
      title: 'Claw9 for Zalo',
      subtitle: 'Fast setup, clean operations, premium support.',
      setupLabel: 'Setup',
      setupPrice: '$79.99',
      monthlyLabel: 'Monthly',
      monthlyPrice: '$30 / month',
      button: 'Book a Consultation',
      features: [
        '1 AI Agent for 24/7 support + CRM sync',
        '1 AI Agent for daily analytics reports',
        'Easy training via UI',
        'Self-improves continuously',
        'Human takeover anytime',
      ],
      calculatorTitle: 'Cost comparison',
      calculatorSubtitle: 'See your current cost next to Claw9.',
      repCount: 'Support reps',
      crmToggle: 'Paying for a separate CRM',
      currentStack: 'Current stack',
      claw9Stack: 'With Claw9',
      repCost: 'Support reps',
      crmCost: 'CRM',
      adminCost: 'Manual ops',
      monthlyTotal: 'Total / month',
      firstMonth: 'First month',
      savings: 'Monthly savings',
      comparisonTitle: 'Why Claw9 is the better choice',
      comparisonRows: [
        ['Zalo responses', 'Staff-based coverage', 'AI coverage 24/7'],
        ['CRM', 'Separate tool', 'Included'],
        ['Bot training', 'Hard to maintain', 'Simple UI'],
        ['Improvement over time', 'Operator dependent', 'Self-improving'],
        ['Human intervention', 'Manual handoff', 'Anytime'],
      ],
    },
    footer: {
      companyName: 'CLAW9 VIETNAM CO., LTD',
      address: '104 Mai Thi Luu, Tan Dinh Ward, Ho Chi Minh City',
      product: 'Product',
      features: 'Features',
      pricing: 'Pricing',
      integrations: 'Integrations',
      company: 'Company',
      about: 'About Us',
      careers: 'Careers',
      contact: 'Contact',
      resources: 'Resources',
      docs: 'Documentation',
      blog: 'Blog',
      support: 'Support',
      legal: 'Legal',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      copyright: '© 2026 Claw9.ai. All rights reserved.'
    }
  }
};

const LandingPage = ({ onLoginClick, onSignupClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState('vi');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [repCount, setRepCount] = useState(1);
  const [hasSeparateCRM, setHasSeparateCRM] = useState(true);

  const t = translations[language];
  const isVietnamese = language === 'vi';
  const currentRepCost = repCount * (isVietnamese ? 10000000 : 400);
  const currentCrmCost = hasSeparateCRM ? (isVietnamese ? 500000 : 20) : 0;
  const currentAdminCost = isVietnamese ? 1000000 : 40;
  const currentMonthlyTotal = currentRepCost + currentCrmCost + currentAdminCost;
  const claw9MonthlyTotal = isVietnamese ? 750000 : 30;
  const claw9FirstMonth = isVietnamese ? 2750000 : 109.99;
  const monthlySavings = currentMonthlyTotal - claw9MonthlyTotal;
  const formatCurrency = (value) => {
    if (isVietnamese) {
      return `${new Intl.NumberFormat('vi-VN').format(value)}đ`;
    }
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(value)}`;
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#050810] text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .gradient-text {
          background: linear-gradient(135deg, #ff4d4d 0%, #ff6b6b 50%, #00e5cc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .gradient-text-red {
          background: linear-gradient(135deg, #ff4d4d 0%, #ff6b6b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .glow-box {
          background: linear-gradient(135deg, rgba(255, 77, 77, 0.1) 0%, rgba(255, 77, 77, 0.05) 100%);
          border: 1px solid rgba(255, 77, 77, 0.2);
          backdrop-filter: blur(20px);
        }

        .glow-box-cyan {
          background: linear-gradient(135deg, rgba(0, 229, 204, 0.1) 0%, rgba(0, 229, 204, 0.05) 100%);
          border: 1px solid rgba(0, 229, 204, 0.2);
          backdrop-filter: blur(20px);
        }

        .hero-glow {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(255, 77, 77, 0.15) 0%, transparent 70%);
        }

        .card-hover {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover:hover {
          transform: translateY(-8px);
          border-color: rgba(255, 77, 77, 0.4);
          box-shadow: 0 20px 40px rgba(255, 77, 77, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #ff4d4d 0%, #ff6b6b 100%);
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: scale(1.02);
          box-shadow: 0 10px 30px rgba(255, 77, 77, 0.3);
        }

        .btn-secondary {
          background: transparent;
          border: 1px solid rgba(255, 77, 77, 0.4);
          transition: all 0.3s ease;
        }

        .btn-secondary:hover {
          background: rgba(255, 77, 77, 0.1);
          border-color: rgba(255, 77, 77, 0.6);
        }

        .floating {
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .slide-up {
          animation: slideUp 0.8s ease-out forwards;
          opacity: 0;
          transform: translateY(40px);
        }

        @keyframes slideUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .stagger-1 { animation-delay: 0.1s; }
        .stagger-2 { animation-delay: 0.2s; }
        .stagger-3 { animation-delay: 0.3s; }
        .stagger-4 { animation-delay: 0.4s; }
        .stagger-5 { animation-delay: 0.5s; }

        .grid-bg {
          background-image:
            linear-gradient(rgba(255, 77, 77, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 77, 77, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }

        .claw-mascot {
          animation: float 4s ease-in-out infinite, pulse-glow 3s ease-in-out infinite;
        }

        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(255, 77, 77, 0.4)); }
          50% { filter: drop-shadow(0 0 40px rgba(255, 77, 77, 0.7)); }
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#050810]/90 backdrop-blur-md transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />

          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('features')}
              className="text-white/70 hover:text-white font-medium transition-colors"
            >
              {t.nav.features}
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="text-white/70 hover:text-white font-medium transition-colors"
            >
              {t.nav.howItWorks}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-white/70 hover:text-white font-medium transition-colors"
            >
              {t.nav.pricing}
            </button>

            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <Globe className="w-4 h-4 text-[#ff4d4d]" />
                <span className="text-white/80">{language === 'vi' ? 'Tiếng Việt' : 'English'}</span>
                <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isLangDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-[#0d1424] border border-white/10 rounded-lg overflow-hidden shadow-xl">
                  <button
                    onClick={() => { setLanguage('vi'); setIsLangDropdownOpen(false); }}
                    className={`w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-2 ${language === 'vi' ? 'text-[#ff4d4d]' : 'text-white/80'}`}
                  >
                    <span>🇻🇳</span> Tiếng Việt
                  </button>
                  <button
                    onClick={() => { setLanguage('en'); setIsLangDropdownOpen(false); }}
                    className={`w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-2 ${language === 'en' ? 'text-[#ff4d4d]' : 'text-white/80'}`}
                  >
                    <span>🇺🇸</span> English
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={onLoginClick}
              className="text-white/70 hover:text-white font-medium transition-colors"
            >
              {language === 'vi' ? 'Đăng Nhập' : 'Login'}
            </button>

            <button
              onClick={onSignupClick || (() => setShowContactForm(true))}
              className="btn-primary px-6 py-2.5 rounded-full font-semibold text-white"
            >
              {t.nav.getStarted}
            </button>
          </div>

          <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#050810] border-b border-white/10 p-6 md:hidden">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('features')} className="text-white/80 hover:text-white font-medium py-2 text-left">
                {t.nav.features}
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-white/80 hover:text-white font-medium py-2 text-left">
                {t.nav.howItWorks}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-white/80 hover:text-white font-medium py-2 text-left">
                {t.nav.pricing}
              </button>
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => setLanguage('vi')}
                  className={`flex-1 py-2 rounded-lg ${language === 'vi' ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]' : 'bg-white/5 text-white/60'}`}
                >
                  🇻🇳 Tiếng Việt
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`flex-1 py-2 rounded-lg ${language === 'en' ? 'bg-[#ff4d4d]/20 text-[#ff4d4d]' : 'bg-white/5 text-white/60'}`}
                >
                  🇺🇸 English
                </button>
              </div>
              <button
                onClick={() => { onLoginClick?.(); setIsMobileMenuOpen(false); }}
                className="w-full py-3 rounded-lg text-white font-bold mt-2 border border-white/20 hover:bg-white/5"
              >
                {language === 'vi' ? 'Đăng Nhập' : 'Login'}
              </button>
              <button
                onClick={() => { onSignupClick?.() || setShowContactForm(true); setIsMobileMenuOpen(false); }}
                className="btn-primary w-full py-3 rounded-lg text-white font-bold mt-2"
              >
                {t.nav.getStarted}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-6 hero-glow grid-bg">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`${isVisible ? 'slide-up stagger-1' : 'opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff4d4d]/10 border border-[#ff4d4d]/30 mb-6">
              <span className="w-2 h-2 bg-[#ff4d4d] rounded-full animate-pulse"></span>
              <span className="text-[#ff4d4d] font-medium text-sm">{t.hero.badge}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6">
              <span className="text-white">{t.hero.title1}</span>
              <br />
              <span className="gradient-text-red">{t.hero.title2}</span>
            </h1>

            <p className="text-lg md:text-xl text-white/60 max-w-xl mb-8 leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button onClick={() => setShowContactForm(true)} className="btn-primary px-8 py-4 rounded-full text-lg font-semibold text-white flex items-center gap-2">
                {t.hero.cta1}
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="btn-secondary px-8 py-4 rounded-full text-lg font-semibold text-white flex items-center gap-2">
                <Play className="w-5 h-5" />
                {t.hero.cta2}
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { value: t.hero.stat1, label: t.hero.stat1Label },
                { value: t.hero.stat2, label: t.hero.stat2Label },
                { value: t.hero.stat3, label: t.hero.stat3Label },
                { value: t.hero.stat4, label: t.hero.stat4Label }
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/50 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Mascot */}
          <div className={`relative flex items-center justify-center ${isVisible ? 'slide-up stagger-3' : 'opacity-0'}`}>
            <div className="relative w-80 h-80 md:w-96 md:h-96">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#ff4d4d]/20 to-[#00e5cc]/20 rounded-full blur-3xl"></div>

              {/* Mascot SVG */}
              <svg viewBox="0 0 200 200" className="w-full h-full claw-mascot relative z-10">
                <defs>
                  <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff4d4d" />
                    <stop offset="50%" stopColor="#ff6b6b" />
                    <stop offset="100%" stopColor="#ff8585" />
                  </linearGradient>
                  <linearGradient id="clawGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ff3333" />
                    <stop offset="100%" stopColor="#ff6b6b" />
                  </linearGradient>
                </defs>

                {/* Body */}
                <ellipse cx="100" cy="120" rx="50" ry="40" fill="url(#bodyGradient)" />

                {/* Left Claw */}
                <path d="M40 90 Q20 70, 25 45 Q30 50, 40 60 Q35 65, 40 75 Q50 85, 50 95 Z" fill="url(#clawGradient2)" />
                <path d="M35 75 Q15 60, 20 35 Q25 45, 35 55 Z" fill="url(#clawGradient2)" />

                {/* Right Claw */}
                <path d="M160 90 Q180 70, 175 45 Q170 50, 160 60 Q165 65, 160 75 Q150 85, 150 95 Z" fill="url(#clawGradient2)" />
                <path d="M165 75 Q185 60, 180 35 Q175 45, 165 55 Z" fill="url(#clawGradient2)" />

                {/* Eyes */}
                <ellipse cx="85" cy="110" rx="12" ry="14" fill="#050810" />
                <ellipse cx="115" cy="110" rx="12" ry="14" fill="#050810" />
                <circle cx="88" cy="107" r="5" fill="white" />
                <circle cx="118" cy="107" r="5" fill="white" />
                <circle cx="89" cy="106" r="2" fill="#050810" />
                <circle cx="119" cy="106" r="2" fill="#050810" />

                {/* Happy Mouth */}
                <path d="M85 130 Q100 145, 115 130" stroke="#050810" strokeWidth="3" fill="none" strokeLinecap="round" />

                {/* Legs */}
                <ellipse cx="75" cy="155" rx="12" ry="8" fill="url(#bodyGradient)" />
                <ellipse cx="125" cy="155" rx="12" ry="8" fill="url(#bodyGradient)" />
                <ellipse cx="90" cy="160" rx="10" ry="6" fill="url(#bodyGradient)" />
                <ellipse cx="110" cy="160" rx="10" ry="6" fill="url(#bodyGradient)" />

                {/* Antenna */}
                <line x1="85" y1="80" x2="75" y2="60" stroke="url(#bodyGradient)" strokeWidth="4" strokeLinecap="round" />
                <line x1="115" y1="80" x2="125" y2="60" stroke="url(#bodyGradient)" strokeWidth="4" strokeLinecap="round" />
                <circle cx="75" cy="58" r="6" fill="#00e5cc" />
                <circle cx="125" cy="58" r="6" fill="#00e5cc" />
              </svg>

              {/* Floating Elements */}
              <div className="absolute top-0 right-0 w-16 h-16 bg-[#ff4d4d]/10 border border-[#ff4d4d]/20 rounded-2xl flex items-center justify-center floating" style={{ animationDelay: '0.5s' }}>
                <MessageSquare className="w-8 h-8 text-[#ff4d4d]" />
              </div>
              <div className="absolute bottom-10 left-0 w-14 h-14 bg-[#00e5cc]/10 border border-[#00e5cc]/20 rounded-2xl flex items-center justify-center floating" style={{ animationDelay: '1s' }}>
                <Bot className="w-7 h-7 text-[#00e5cc]" />
              </div>
              <div className="absolute top-20 left-0 w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center floating" style={{ animationDelay: '1.5s' }}>
                <Zap className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-white/40 text-sm">Scroll</span>
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/40 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-gradient-to-b from-[#050810] to-[#0a0f1c]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text-red">{t.features.title}</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageCircle, ...t.features.feature1 },
              { icon: ShoppingCart, ...t.features.feature2 },
              { icon: MessageSquare, ...t.features.feature3 },
              { icon: TrendingUp, ...t.features.feature4 },
              { icon: Globe, ...t.features.feature5 },
              { icon: Shield, ...t.features.feature6 }
            ].map((feature, i) => (
              <div key={i} className="glow-box rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 bg-[#ff4d4d]/20 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#ff4d4d]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 bg-[#0a0f1c]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text-red">{t.howItWorks.title}</span>
            </h2>
            <p className="text-white/60 text-lg">
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: MessageSquare, ...t.howItWorks.step1 },
              { step: '02', icon: Bot, ...t.howItWorks.step2 },
              { step: '03', icon: Zap, ...t.howItWorks.step3 }
            ].map((item, i) => (
              <div key={i} className="relative text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-[#ff4d4d]/20 to-[#ff4d4d]/5 rounded-3xl flex items-center justify-center border border-[#ff4d4d]/20">
                    <item.icon className="w-10 h-10 text-[#ff4d4d]" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#ff4d4d] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-white/60">{item.desc}</p>

                {i < 2 && (
                  <div className="hidden md:block absolute top-12 right-0 translate-x-1/2 w-16">
                    <ChevronRight className="w-8 h-8 text-[#ff4d4d]/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#0a0f1c] to-[#050810]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              {t.useCases.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShoppingCart, color: '#ff4d4d', ...t.useCases.case1 },
              { icon: Headphones, color: '#00e5cc', ...t.useCases.case2 },
              { icon: Users, color: '#fbbf24', ...t.useCases.case3 },
              { icon: Clock, color: '#a78bfa', ...t.useCases.case4 }
            ].map((useCase, i) => (
              <div key={i} className="glow-box rounded-2xl p-6 card-hover text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${useCase.color}20` }}>
                  <useCase.icon className="w-8 h-8" style={{ color: useCase.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{useCase.title}</h3>
                <p className="text-white/60 text-sm">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6 bg-[#050810]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="gradient-text-red">{t.testimonials.title}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[t.testimonials.testimonial1, t.testimonials.testimonial2, t.testimonials.testimonial3].map((testimonial, i) => (
              <div key={i} className="glow-box rounded-2xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-[#ff4d4d] text-[#ff4d4d]" />
                  ))}
                </div>
                <p className="text-white/80 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <div className="text-white font-semibold">{testimonial.author}</div>
                    <div className="text-white/50 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="pricing" className="py-24 px-6 bg-gradient-to-b from-[#050810] to-[#0a0f1c]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glow-box rounded-3xl p-12 md:p-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/60 text-sm mb-6">
              {t.pricing.eyebrow}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.pricing.title}
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              {t.pricing.subtitle}
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-10 text-left">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="text-white/45 text-sm mb-2">{t.pricing.setupLabel}</div>
                <div className="text-3xl font-bold text-white">{t.pricing.setupPrice}</div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="text-white/45 text-sm mb-2">{t.pricing.monthlyLabel}</div>
                <div className="text-3xl font-bold text-white">{t.pricing.monthlyPrice}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 text-left mb-10 max-w-3xl mx-auto">
              {t.pricing.features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-white/75">
                  <Check className="w-5 h-5 text-[#00e5cc] flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6 text-left mb-10">
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t.pricing.calculatorTitle}</h3>
                <p className="text-white/55 mb-6">{t.pricing.calculatorSubtitle}</p>

                <div className="space-y-5 mb-6">
                  <div>
                    <div className="flex items-center justify-between text-sm text-white/70 mb-2">
                      <span>{t.pricing.repCount}</span>
                      <span>{repCount}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={repCount}
                      onChange={(e) => setRepCount(Number(e.target.value))}
                      className="w-full accent-[#ff4d4d]"
                    />
                  </div>

                  <label className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#050810]/60 px-4 py-3 cursor-pointer">
                    <span className="text-white/70">{t.pricing.crmToggle}</span>
                    <input
                      type="checkbox"
                      checked={hasSeparateCRM}
                      onChange={(e) => setHasSeparateCRM(e.target.checked)}
                      className="w-4 h-4 accent-[#ff4d4d]"
                    />
                  </label>
                </div>

                <div className="grid gap-3">
                  <div className="rounded-2xl border border-white/10 bg-[#050810]/60 p-4">
                    <div className="text-white/45 text-sm mb-3">{t.pricing.currentStack}</div>
                    <div className="space-y-2 text-sm text-white/70">
                      <div className="flex justify-between"><span>{t.pricing.repCost}</span><span>{formatCurrency(currentRepCost)}</span></div>
                      <div className="flex justify-between"><span>{t.pricing.crmCost}</span><span>{formatCurrency(currentCrmCost)}</span></div>
                      <div className="flex justify-between"><span>{t.pricing.adminCost}</span><span>{formatCurrency(currentAdminCost)}</span></div>
                    </div>
                    <div className="flex justify-between mt-4 pt-4 border-t border-white/10 text-white font-semibold">
                      <span>{t.pricing.monthlyTotal}</span>
                      <span>{formatCurrency(currentMonthlyTotal)}</span>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#ff4d4d]/30 bg-[#ff4d4d]/10 p-4">
                    <div className="text-[#ffb3b3] text-sm mb-3">{t.pricing.claw9Stack}</div>
                    <div className="space-y-2 text-sm text-white/80">
                      <div className="flex justify-between"><span>{t.pricing.monthlyTotal}</span><span>{formatCurrency(claw9MonthlyTotal)}</span></div>
                      <div className="flex justify-between"><span>{t.pricing.firstMonth}</span><span>{formatCurrency(claw9FirstMonth)}</span></div>
                    </div>
                    <div className="flex justify-between mt-4 pt-4 border-t border-[#ff4d4d]/20 text-white font-semibold">
                      <span>{t.pricing.savings}</span>
                      <span className="text-[#00e5cc]">{formatCurrency(monthlySavings)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
                <h3 className="text-2xl font-bold text-white mb-6">{t.pricing.comparisonTitle}</h3>
                <div className="space-y-3">
                  {t.pricing.comparisonRows.map(([label, oldWay, claw9Way], i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr] gap-3 rounded-2xl border border-white/10 bg-[#050810]/60 p-4">
                      <div className="text-white font-medium">{label}</div>
                      <div className="text-white/45">{oldWay}</div>
                      <div className="text-[#00e5cc] font-medium">{claw9Way}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={() => setShowContactForm(true)} className="btn-primary px-10 py-4 rounded-full text-lg font-semibold text-white">
              {t.pricing.button}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-16 px-6 bg-[#050810]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Logo size="small" />
              <p className="text-white font-medium mt-4 text-sm">
                {t.footer.companyName}
              </p>
              <p className="text-white/50 mt-1 text-sm">
                {t.footer.address}
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.product}</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.features}</a></li>
                <li><a href="#pricing" className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.pricing}</a></li>
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.integrations}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.company}</h4>
              <ul className="space-y-2">
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.about}</button></li>
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.careers}</button></li>
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.contact}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t.footer.resources}</h4>
              <ul className="space-y-2">
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.docs}</button></li>
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.blog}</button></li>
                <li><button className="text-white/50 hover:text-white transition-colors text-sm">{t.footer.support}</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">{t.footer.copyright}</p>
            <div className="flex items-center gap-6">
              <a
                href="/terms"
                className="text-white/40 hover:text-white text-sm transition-colors"
              >
                {t.footer.terms}
              </a>
              <a
                href="/privacy"
                className="text-white/40 hover:text-white text-sm transition-colors"
              >
                {t.footer.privacy}
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0d1424] rounded-2xl shadow-2xl w-full max-w-md border border-white/10">
            <div className="bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-white font-semibold text-lg">
                {language === 'vi' ? 'Đăng Ký Dùng Thử' : 'Sign Up For Trial'}
              </h2>
              <button onClick={() => setShowContactForm(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form className="p-6 space-y-4">
              <div>
                <label className="block text-white/80 text-sm mb-2">
                  {language === 'vi' ? 'Họ và tên' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
                  placeholder={language === 'vi' ? 'Nguyễn Văn A' : 'John Doe'}
                />
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">Email *</label>
                <input
                  type="email"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">
                  {language === 'vi' ? 'Số điện thoại' : 'Phone'} *
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
                  placeholder="0912 345 678"
                />
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">
                  {language === 'vi' ? 'Tên doanh nghiệp' : 'Company Name'}
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
                  placeholder={language === 'vi' ? 'Công ty ABC' : 'ABC Company'}
                />
              </div>

              <button
                type="submit"
                className="w-full btn-primary py-4 rounded-lg font-semibold text-white mt-4"
              >
                {language === 'vi' ? 'Đăng Ký Ngay' : 'Sign Up Now'}
              </button>

              <p className="text-white/40 text-xs text-center">
                {language === 'vi' ? (
                  <>
                    Bằng việc đăng ký, bạn đồng ý với{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff4d4d] hover:underline"
                    >
                      Điều khoản dịch vụ
                    </a>{' '}
                    và{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff4d4d] hover:underline"
                    >
                      Chính sách bảo mật
                    </a>{' '}
                    của chúng tôi.
                  </>
                ) : (
                  <>
                    By signing up, you agree to our{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff4d4d] hover:underline"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff4d4d] hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </>
                )}
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
